import React, { useState, useEffect } from 'react';
import { ArrowLeft, MessageSquare, User, Hash, Calendar, Sparkles, Play, Heart, Eye, Send } from 'lucide-react';
import { useIsMobile } from '../hooks/useIsMobile.ts';
import { API_URL } from '../config/api.ts';
import LoginModal from './LoginModal.tsx';

interface Character {
  _id: string;
  name: string;
  role?: string;
  description: string;
  personality: string;
  systemPrompt: string;
  avatar: string;
  backgroundImage: string;
  tags: string[];
  isDefault: boolean;
  createdBy: string;
  hidePersonality?: boolean;
  hideCreator?: boolean;
  stats?: {
    views: number;
    likes: number;
    messagesCount: number;
    favorites?: number;
  };
  userId?: {
    _id: string;
    username: string;
  };
}

interface Chat {
  _id: string;
  title: string;
  characterId: {
    _id: string;
    name: string;
    avatar: string;
    description: string;
  };
  messages: any[];
  createdAt: string;
  updatedAt: string;
}

interface CharacterDetailProps {
  character: Character;
  onBack: () => void;
  onStartChat: (character: Character, chatId?: string) => void;
}

const CharacterDetail: React.FC<CharacterDetailProps> = ({
  character,
  onBack,
  onStartChat
}) => {
  const isMobile = useIsMobile();
  const [isStartingChat, setIsStartingChat] = useState(false);
  const [existingChats, setExistingChats] = useState<Chat[]>([]);
  const [loading, setLoading] = useState(true);
  const [likes, setLikes] = useState(character.stats?.likes || 0);
  const [hasLiked, setHasLiked] = useState(false); // In a real app check backend
  const [views, setViews] = useState(character.stats?.views || 0);
  const [activeTab, setActiveTab] = useState<'info' | 'comments'>('info');
  const [showLoginModal, setShowLoginModal] = useState(false);

  // Comments state
  const [comments, setComments] = useState<{ _id?: string, username: string, text: string, createdAt: string }[]>([]);
  const [newComment, setNewComment] = useState("");
  const [loadingComments, setLoadingComments] = useState(true);
  const [submittingComment, setSubmittingComment] = useState(false);

  // Fetch comments
  useEffect(() => {
    const fetchComments = async () => {
      try {
        setLoadingComments(true);
        const response = await fetch(`${API_URL}/comments/${character._id}`, {
          credentials: 'include'
        });
        const data = await response.json();
        if (data.success) {
          setComments(data.comments);
        }
      } catch (error) {
        console.error('Error fetching comments:', error);
      } finally {
        setLoadingComments(false);
      }
    };
    fetchComments();
  }, [character._id]);

  // Fetch like status on load
  useEffect(() => {
    const fetchLikeStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/characters/${character._id}/like-status`, {
          credentials: 'include'
        });
        const data = await response.json();
        if (data.success) {
          setHasLiked(data.hasLiked);
        } else {
          // User not authenticated or error - default to not liked
          setHasLiked(false);
        }
      } catch (error) {
        console.error('Error fetching like status:', error);
        setHasLiked(false);
      }
    };
    fetchLikeStatus();
  }, [character._id]);

  const handleLike = async () => {
    try {
      const response = await fetch(`${API_URL}/characters/${character._id}/like`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setLikes(data.likes);
        setHasLiked(data.liked);
      } else if (response.status === 401 || data.message === 'Authentication required' || data.message === 'Autenticación requerida') {
        // User not logged in - show login modal
        setShowLoginModal(true);
      }
    } catch (error) {
      console.error('Error liking:', error);
    }
  };

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || submittingComment) return;

    setSubmittingComment(true);
    try {
      const response = await fetch(`${API_URL}/comments/${character._id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ text: newComment }),
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success && data.comment) {
        setComments([data.comment, ...comments]);
        setNewComment("");
      } else {
        alert(data.message || 'Error adding comment');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Error adding comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  useEffect(() => {
    // Increment views
    const incrementView = async () => {
      try {
        await fetch(`${API_URL}/characters/${character._id}/view`, {
          method: 'POST',
          credentials: 'include'
        });
        // We don't necessarily need to update local views immediately as it's just +1
        // but we can for feedback
        setViews(prev => prev + 1);
      } catch (e) {
        console.error("Error incrementing view", e);
      }
    };
    incrementView();
  }, [character._id]);

  useEffect(() => {
    const loadExistingChats = async () => {
      try {
        console.log('🔍 [DEBUG] Cargando chats para personaje:', character.name, 'ID:', character._id);
        const response = await fetch(`${API_URL}/chats`, {
          credentials: 'include'
        });
        const data = await response.json();

        console.log('🔍 [DEBUG] Respuesta de API chats:', data);

        if (data.success) {
          console.log('🔍 [DEBUG] Total de chats del usuario:', data.chats.length);

          // Filtrar chats de este personaje
          const characterChats = data.chats.filter((chat: Chat) => {
            const hasCharacterId = chat.characterId && chat.characterId._id === character._id;
            console.log('🔍 [DEBUG] Chat:', chat.title, 'CharacterID:', chat.characterId?._id, 'Match:', hasCharacterId);
            return hasCharacterId;
          });

          console.log('🔍 [DEBUG] Chats filtrados para', character.name + ':', characterChats.length);
          console.log('🔍 [DEBUG] Chats encontrados:', characterChats);

          setExistingChats(characterChats);
          console.log('🔍 [DEBUG] Estado existingChats actualizado a:', characterChats.length, 'chats');
        }
      } catch (error) {
        console.error('Error loading chats:', error);
      } finally {
        setLoading(false);
      }
    };

    loadExistingChats();
  }, [character._id]);

  const handleContinueLastChat = () => {
    if (existingChats.length > 0) {
      // Ordenar por fecha de actualización (más reciente primero)
      // Usamos una copia para no mutar el estado original
      const sortedChats = [...existingChats].sort((a, b) => {
        const dateA = new Date(a.updatedAt || a.createdAt || 0).getTime();
        const dateB = new Date(b.updatedAt || b.createdAt || 0).getTime();
        return dateB - dateA;
      });

      const lastChat = sortedChats[0];
      console.log('▶️ Continuando último chat:', lastChat._id, lastChat.title);
      onStartChat(character, lastChat._id);
    }
  };

  const handleStartChat = async () => {
    setIsStartingChat(true);
    try {
      // Crear nuevo chat con este personaje
      const response = await fetch(`${API_URL}/chats`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ characterId: character._id }),
        credentials: 'include'
      });

      const data = await response.json();

      if (data.success) {
        // Ir al chat
        console.log('✅ Nuevo chat creado en detalle:', data.chat._id);
        onStartChat(character, data.chat._id);
      } else if (response.status === 401 || data.message === 'Not authenticated') {
        // User not logged in - show login modal
        console.log('🔒 Usuario no autenticado, mostrando modal de login');
        setShowLoginModal(true);
      } else {
        console.error('Error creating chat:', data.message);
      }
    } catch (error) {
      console.error('Error creating new chat:', error);
    } finally {
      setIsStartingChat(false);
    }
  };

  // Mobile Layout
  if (isMobile) {
    return (
      <div style={{
        minHeight: '100dvh',
        background: '#1a1a2e',
        color: '#333',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        paddingBottom: '80px' // Space for fixed button
      }}>
        {/* Hero Image Section */}
        <div style={{
          position: 'relative',
          width: '100%',
          height: '300px',
          overflow: 'hidden'
        }}>
          <img
            src={character.avatar || character.backgroundImage}
            alt={character.name}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
          {/* Back Button */}
          <button
            onClick={onBack}
            style={{
              position: 'absolute',
              top: '16px',
              left: '16px',
              background: 'rgba(0,0,0,0.5)',
              border: 'none',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              cursor: 'pointer'
            }}
          >
            <ArrowLeft size={20} />
          </button>
          {/* Gradient overlay with name */}
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            padding: '40px 16px 16px',
            background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
            color: 'white'
          }}>
            <h1 style={{ fontSize: '24px', fontWeight: '700', margin: 0 }}>
              {character.name}
            </h1>
            <p style={{ fontSize: '14px', opacity: 0.8, margin: '4px 0 0' }}>
              {character.role || 'AI Companion'}
            </p>
            <p style={{
              fontSize: '11px',
              opacity: 0.6,
              margin: '4px 0 0',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              Created by
              <span style={{ fontWeight: '600', color: '#ff6b6b' }}>
                {character.hideCreator
                  ? 'Anonymous'
                  : (character.userId && typeof character.userId === 'object' && character.userId.username
                    ? character.userId.username
                    : (character.createdBy === 'system' ? 'System' : 'Unknown'))}
              </span>
            </p>
          </div>
        </div>

        {/* Stats Bar */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-around',
          padding: '16px',
          background: '#2a2a3e',
          borderBottom: '1px solid rgba(255,255,255,0.1)'
        }}>
          <div style={{ textAlign: 'center', color: 'white' }}>
            <MessageSquare size={18} style={{ marginBottom: '4px', opacity: 0.7 }} />
            <div style={{ fontSize: '14px', fontWeight: '600' }}>{character.stats?.messagesCount || 0}</div>
          </div>
          <div style={{ textAlign: 'center', color: 'white' }}>
            <Heart size={18} style={{ marginBottom: '4px', opacity: 0.7 }} />
            <div style={{ fontSize: '14px', fontWeight: '600' }}>{likes}</div>
          </div>
          <div style={{ textAlign: 'center', color: 'white' }}>
            <Sparkles size={18} style={{ marginBottom: '4px', opacity: 0.7 }} />
            <div style={{ fontSize: '14px', fontWeight: '600' }}>{character.stats?.favorites || 0}</div>
          </div>
          <div style={{ textAlign: 'center', color: 'white' }}>
            <Eye size={18} style={{ marginBottom: '4px', opacity: 0.7 }} />
            <div style={{ fontSize: '14px', fontWeight: '600' }}>{views}</div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          background: '#2a2a3e'
        }}>
          <button
            onClick={() => setActiveTab('info')}
            style={{
              flex: 1,
              padding: '14px',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'info' ? '2px solid #a855f7' : '2px solid transparent',
              color: activeTab === 'info' ? '#a855f7' : 'rgba(255,255,255,0.6)',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Info
          </button>
          <button
            onClick={() => setActiveTab('comments')}
            style={{
              flex: 1,
              padding: '14px',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'comments' ? '2px solid #a855f7' : '2px solid transparent',
              color: activeTab === 'comments' ? '#a855f7' : 'rgba(255,255,255,0.6)',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Comments
          </button>
        </div>

        {/* Tab Content */}
        <div style={{ padding: '16px', background: '#1e1e2e' }}>
          {activeTab === 'info' ? (
            <>
              {/* Introduction */}
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px', color: 'white' }}>
                Introduction
              </h3>

              {/* Tags */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
                {character.tags?.map((tag, i) => (
                  <span key={i} style={{
                    background: 'rgba(168, 85, 247, 0.2)',
                    color: '#c084fc',
                    padding: '6px 12px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>
                    {tag}
                  </span>
                ))}
              </div>

              {/* Description */}
              <p style={{
                fontSize: '14px',
                lineHeight: '1.6',
                color: 'rgba(255,255,255,0.8)',
                marginBottom: '20px'
              }}>
                {character.description}
              </p>

              {/* Stats List */}
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '12px',
                fontSize: '13px',
                color: 'rgba(255,255,255,0.6)',
                marginBottom: '20px'
              }}>
                <span>💬 {character.stats?.messagesCount || 0} messages</span>
                <span>❤️ {likes} likes</span>
                <span>⭐ {character.stats?.favorites || 0} favorites</span>
              </div>

              {/* Personality Section */}
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px', color: 'white', marginTop: '24px' }}>
                Personality
              </h3>
              {character.personality === '[Hidden by creator]' ? (
                <div style={{
                  background: 'rgba(99, 102, 241, 0.15)',
                  border: '1px solid rgba(99, 102, 241, 0.3)',
                  borderRadius: '12px',
                  padding: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <span style={{ fontSize: '20px' }}>🔒</span>
                  <p style={{
                    fontSize: '14px',
                    color: 'rgba(165, 180, 252, 0.9)',
                    margin: 0,
                    fontStyle: 'italic'
                  }}>
                    The creator has chosen to keep this character's personality hidden.
                  </p>
                </div>
              ) : (
                <p style={{
                  fontSize: '14px',
                  lineHeight: '1.6',
                  color: 'rgba(255,255,255,0.8)'
                }}>
                  {character.personality}
                </p>
              )}
            </>
          ) : (
            <>
              {/* Comments Section */}
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: 'white' }}>
                Comments
              </h3>

              {/* Comments List */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '16px' }}>
                {loadingComments ? (
                  <p style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>Loading comments...</p>
                ) : comments.length === 0 ? (
                  <p style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>No comments yet. Be the first!</p>
                ) : (
                  comments.map((comment, i) => (
                    <div key={comment._id || i} style={{
                      background: 'rgba(255,255,255,0.05)',
                      borderRadius: '12px',
                      padding: '12px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ fontWeight: '600', color: '#a855f7', fontSize: '14px' }}>
                          {comment.username}
                        </span>
                        <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>
                          {new Date(comment.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.8)', margin: 0 }}>
                        {comment.text}
                      </p>
                    </div>
                  ))
                )}
              </div>

              {/* Add Comment Form */}
              <form onSubmit={handleAddComment} style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Write a comment..."
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    borderRadius: '24px',
                    border: '1px solid rgba(255,255,255,0.1)',
                    background: 'rgba(255,255,255,0.05)',
                    color: 'white',
                    fontSize: '14px'
                  }}
                />
                <button
                  type="submit"
                  disabled={submittingComment || !newComment.trim()}
                  style={{
                    background: '#a855f7',
                    border: 'none',
                    borderRadius: '50%',
                    width: '44px',
                    height: '44px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    cursor: 'pointer',
                    opacity: submittingComment || !newComment.trim() ? 0.5 : 1
                  }}
                >
                  <Send size={18} />
                </button>
              </form>
            </>
          )}
        </div>

        {/* Fixed Buttons */}
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '12px 16px',
          background: 'linear-gradient(transparent, #1a1a2e 20%)',
          paddingTop: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px'
        }}>
          {/* Continue Conversation - only if there are existing chats */}
          {existingChats.length > 0 && (
            <button
              onClick={() => {
                const lastChat = existingChats[0];
                onStartChat(character, lastChat._id);
              }}
              style={{
                width: '100%',
                padding: '14px',
                background: 'linear-gradient(135deg, #a855f7, #6366f1)',
                border: 'none',
                borderRadius: '24px',
                color: 'white',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                boxShadow: '0 4px 16px rgba(168, 85, 247, 0.3)'
              }}
            >
              <Play size={18} />
              Continue Conversation
            </button>
          )}

          {/* New Conversation */}
          <button
            onClick={handleStartChat}
            disabled={isStartingChat}
            style={{
              width: '100%',
              padding: '14px',
              background: existingChats.length > 0
                ? 'rgba(255,255,255,0.1)'
                : 'linear-gradient(135deg, #ec4899, #a855f7)',
              border: existingChats.length > 0 ? '1px solid rgba(255,255,255,0.2)' : 'none',
              borderRadius: '24px',
              color: 'white',
              fontSize: '15px',
              fontWeight: '600',
              cursor: isStartingChat ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              boxShadow: existingChats.length > 0 ? 'none' : '0 4px 20px rgba(168, 85, 247, 0.4)'
            }}
          >
            <MessageSquare size={18} />
            {isStartingChat ? 'Creating...' : 'New Conversation'}
          </button>
        </div>

        {/* Login Modal for mobile */}
        <LoginModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          onLoginSuccess={() => {
            setShowLoginModal(false);
            // Retry starting chat after successful login
            handleStartChat();
          }}
        />
      </div>
    );
  }

  // Desktop Layout (existing)
  return (
    <div style={{
      minHeight: '100dvh',
      background: `linear-gradient(135deg, rgba(26,26,46,0.95) 0%, rgba(15,15,30,0.95) 100%), url(${character.backgroundImage})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed',
      color: 'white',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      position: 'relative'
    }}>
      {/* Overlay para mejor legibilidad */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.4)',
        backdropFilter: 'blur(2px)'
      }}></div>

      {/* Header con botón de volver */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        padding: '20px',
        position: 'relative',
        zIndex: 2
      }}>
        <button
          onClick={onBack}
          style={{
            background: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: '12px',
            padding: '12px',
            color: 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'all 0.3s ease',
            backdropFilter: 'blur(10px)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
            e.currentTarget.style.transform = 'translateX(-4px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
            e.currentTarget.style.transform = 'translateX(0)';
          }}
        >
          <ArrowLeft size={20} />
          Back to Gallery
        </button>
      </div>

      {/* Contenido principal */}
      <div style={{
        padding: isMobile ? '12px' : '20px',
        maxWidth: '1200px',
        margin: '0 auto',
        position: 'relative',
        zIndex: 2
      }}>
        <div style={{
          display: isMobile ? 'flex' : 'grid',
          flexDirection: 'column',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr',
          gap: isMobile ? '20px' : '40px',
          alignItems: 'start'
        }}>
          {/* Panel izquierdo - Avatar y datos básicos */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(58,58,78,0.9), rgba(42,42,62,0.9))',
            borderRadius: isMobile ? '16px' : '24px',
            padding: isMobile ? '20px' : '32px',
            border: '1px solid rgba(255,255,255,0.1)',
            boxShadow: '0 12px 48px rgba(0,0,0,0.3)',
            backdropFilter: 'blur(20px)',
            position: isMobile ? 'relative' : 'sticky',
            top: isMobile ? 'auto' : '20px',
            width: '100%'
          }}>
            {/* Avatar principal */}
            <div style={{
              width: '160px',
              height: '160px',
              borderRadius: '50%',
              overflow: 'hidden',
              margin: '0 auto 24px',
              border: '4px solid rgba(255,107,107,0.3)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
              position: 'relative'
            }}>
              <img
                src={character.avatar}
                alt={character.name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
              {/* Badge de estado */}
              {character.isDefault && (
                <div style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  background: 'linear-gradient(135deg, #ff6b6b, #ff4757)',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '12px',
                  fontSize: '10px',
                  fontWeight: '600',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                }}>
                  <Sparkles size={10} style={{ marginRight: '2px' }} />
                  Popular
                </div>
              )}
            </div>

            {/* Nombre y título */}
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <h1 style={{
                fontSize: '32px',
                fontWeight: '800',
                marginBottom: '8px',
                background: 'linear-gradient(135deg, #ff6b6b, #ff4757)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                {character.name}
              </h1>
              {character.role && (
                <p style={{
                  fontSize: '14px',
                  color: 'rgba(255,255,255,0.6)',
                  marginBottom: '8px',
                  fontStyle: 'italic'
                }}>
                  {character.role}
                </p>
              )}

              {/* Created By */}
              <p style={{
                fontSize: '12px',
                color: 'rgba(255,255,255,0.4)',
                margin: '-4px 0 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px'
              }}>
                Created by
                <span style={{
                  color: '#ff6b6b',
                  fontWeight: '600',
                  opacity: 0.9
                }}>
                  {character.hideCreator
                    ? 'Anonymous'
                    : (character.userId && typeof character.userId === 'object' && character.userId.username
                      ? character.userId.username
                      : (character.createdBy === 'system' ? 'System' : 'Unknown'))}
                </span>
              </p>
              <p style={{
                fontSize: '16px',
                color: 'rgba(255,255,255,0.8)',
                lineHeight: '1.5'
              }}>
                {character.description.length > 150
                  ? character.description.substring(0, 150) + '...'
                  : character.description
                }
              </p>
            </div>

            {/* Tags */}
            <div style={{ marginBottom: '32px' }}>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px'
              }}>
                {character.tags.map((tag, index) => (
                  <span
                    key={index}
                    style={{
                      background: 'linear-gradient(135deg, rgba(255,107,107,0.2), rgba(255,71,87,0.2))',
                      border: '1px solid rgba(255,107,107,0.3)',
                      color: '#ff8585',
                      padding: '8px 12px',
                      borderRadius: '16px',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Botones de chat */}
            {loading ? (
              <div style={{
                width: '100%',
                padding: '16px',
                textAlign: 'center',
                color: 'rgba(255,255,255,0.7)'
              }}>
                Loading conversations...
              </div>
            ) : existingChats.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {/* Continuar última conversación */}
                <button
                  onClick={handleContinueLastChat}
                  disabled={isStartingChat}
                  style={{
                    width: '100%',
                    background: 'linear-gradient(135deg, #4CAF50, #45a049)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '16px',
                    padding: '16px 24px',
                    fontSize: '16px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 8px 24px rgba(76,175,80,0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '12px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 12px 36px rgba(76,175,80,0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(76,175,80,0.3)';
                  }}
                >
                  <Play size={20} />
                  Continue Conversation
                </button>

                {/* Comenzar nueva conversación */}
                <button
                  onClick={handleStartChat}
                  disabled={isStartingChat}
                  style={{
                    width: '100%',
                    background: isStartingChat
                      ? 'linear-gradient(135deg, rgba(255,107,107,0.5), rgba(255,71,87,0.5))'
                      : 'linear-gradient(135deg, #ff6b6b, #ff4757)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '16px',
                    padding: '12px 24px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: isStartingChat ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 6px 18px rgba(255,107,107,0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    if (!isStartingChat) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(255,107,107,0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isStartingChat) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 6px 18px rgba(255,107,107,0.3)';
                    }
                  }}
                >
                  {isStartingChat ? (
                    <>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid rgba(255,255,255,0.3)',
                        borderTop: '2px solid white',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <MessageSquare size={16} />
                      New Conversation
                    </>
                  )}
                </button>
              </div>
            ) : (
              /* Comenzar primera conversación */
              <button
                onClick={handleStartChat}
                disabled={isStartingChat}
                style={{
                  width: '100%',
                  background: isStartingChat
                    ? 'linear-gradient(135deg, rgba(255,107,107,0.5), rgba(255,71,87,0.5))'
                    : 'linear-gradient(135deg, #ff6b6b, #ff4757)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '16px',
                  padding: '16px 24px',
                  fontSize: '16px',
                  fontWeight: '700',
                  cursor: isStartingChat ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 8px 24px rgba(255,107,107,0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px'
                }}
                onMouseEnter={(e) => {
                  if (!isStartingChat) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 12px 36px rgba(255,107,107,0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isStartingChat) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(255,107,107,0.3)';
                  }
                }}
              >
                {isStartingChat ? (
                  <>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Creating Chat...
                  </>
                ) : (
                  <>
                    <MessageSquare size={20} />
                    Start Conversation
                  </>
                )}
              </button>
            )}
          </div>

          {/* Panel derecho - Información detallada */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: isMobile ? '16px' : '24px',
            width: '100%'
          }}>
            {/* Section ordering: Desktop shows Introduction first, Mobile shows Personality first */}

            {/* Introduction Section - First on Desktop */}
            {!isMobile && (
              <div style={{
                background: 'linear-gradient(135deg, rgba(58,58,78,0.9), rgba(42,42,62,0.9))',
                borderRadius: '20px',
                padding: '24px',
                border: '1px solid rgba(255,255,255,0.1)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                backdropFilter: 'blur(20px)'
              }}>
                <h3 style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  fontSize: '20px',
                  fontWeight: '700',
                  marginBottom: '16px',
                  color: '#6bbfff'
                }}>
                  <Sparkles size={24} />
                  Introduction
                </h3>
                <div style={{
                  background: 'rgba(0,0,0,0.2)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '12px',
                  padding: '16px',
                  fontSize: '14px',
                  lineHeight: '1.6',
                  color: 'rgba(255,255,255,0.8)',
                  maxHeight: '300px',
                  overflowY: 'auto'
                }}>
                  {character.description}
                </div>
              </div>
            )}

            {/* Personality Section */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(58,58,78,0.9), rgba(42,42,62,0.9))',
              borderRadius: isMobile ? '16px' : '20px',
              padding: isMobile ? '16px' : '24px',
              border: '1px solid rgba(255,255,255,0.1)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              backdropFilter: 'blur(20px)'
            }}>
              <h3 style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                fontSize: '20px',
                fontWeight: '700',
                marginBottom: '16px',
                color: '#ff6b6b'
              }}>
                <User size={24} />
                Personality
              </h3>
              <div style={{
                background: isMobile ? 'transparent' : 'rgba(0,0,0,0.2)',
                border: isMobile ? 'none' : '1px solid rgba(255,255,255,0.1)',
                borderRadius: '12px',
                padding: isMobile ? '0' : '16px',
                fontSize: '15px',
                lineHeight: '1.7',
                color: 'rgba(255,255,255,0.9)',
                maxHeight: isMobile ? 'none' : '300px',
                overflowY: isMobile ? 'visible' : 'auto'
              }}>
                {character.personality}
              </div>
            </div>

            {/* Introduction Section - Second on Mobile only */}
            {isMobile && (
              <div style={{
                background: 'linear-gradient(135deg, rgba(58,58,78,0.9), rgba(42,42,62,0.9))',
                borderRadius: '16px',
                padding: '16px',
                border: '1px solid rgba(255,255,255,0.1)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                backdropFilter: 'blur(20px)'
              }}>
                <h3 style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  fontSize: '20px',
                  fontWeight: '700',
                  marginBottom: '16px',
                  color: '#6bbfff'
                }}>
                  <Sparkles size={24} />
                  Introduction
                </h3>
                <div style={{
                  background: 'rgba(0,0,0,0.2)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '12px',
                  padding: '16px',
                  fontSize: '14px',
                  lineHeight: '1.6',
                  color: 'rgba(255,255,255,0.8)',
                  maxHeight: '300px',
                  overflowY: 'auto'
                }}>
                  {character.description}
                </div>
              </div>
            )}

            {/* Stats & Actions */}
            <div style={{
              display: 'flex',
              gap: '16px',
              marginBottom: '24px'
            }}>
              <button
                onClick={handleLike}
                style={{
                  flex: 1,
                  background: hasLiked ? 'rgba(255, 107, 107, 0.2)' : 'rgba(255,255,255,0.05)',
                  border: `1px solid ${hasLiked ? '#ff6b6b' : 'rgba(255,255,255,0.1)'}`,
                  borderRadius: '16px',
                  padding: '16px',
                  color: hasLiked ? '#ff6b6b' : 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontWeight: '600'
                }}>
                <Heart size={20} fill={hasLiked ? "#ff6b6b" : "none"} />
                {likes} Likes
              </button>

              <div style={{
                flex: 1,
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '16px',
                padding: '16px',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                fontWeight: '600'
              }}>
                <Eye size={20} />
                {views} Views
              </div>
            </div>

            {/* Comments Section */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(58,58,78,0.9), rgba(42,42,62,0.9))',
              borderRadius: isMobile ? '16px' : '20px',
              padding: isMobile ? '16px' : '24px',
              border: '1px solid rgba(255,255,255,0.1)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              backdropFilter: 'blur(20px)'
            }}>
              <h3 style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                fontSize: '20px',
                fontWeight: '700',
                marginBottom: '16px',
                color: '#ffd93d'
              }}>
                <MessageSquare size={24} />
                Comments
              </h3>

              <div style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {loadingComments ? (
                  <p style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>Loading comments...</p>
                ) : comments.length === 0 ? (
                  <p style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>No comments yet. Be the first!</p>
                ) : (
                  comments.map((c, i) => (
                    <div key={c._id || i} style={{
                      padding: '12px',
                      background: 'rgba(0,0,0,0.2)',
                      borderRadius: '12px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span style={{ fontWeight: '600', fontSize: '14px', color: '#6bbfff' }}>{c.username}</span>
                        <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>
                          {new Date(c.createdAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <p style={{ margin: 0, fontSize: '14px', color: 'rgba(255,255,255,0.9)' }}>{c.text}</p>
                    </div>
                  ))
                )}
              </div>

              <form onSubmit={handleAddComment} style={{ display: 'flex', gap: '8px' }}>
                <input
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Write a comment..."
                  disabled={submittingComment}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    borderRadius: '12px',
                    border: '1px solid rgba(255,255,255,0.1)',
                    background: 'rgba(0,0,0,0.2)',
                    color: 'white',
                    outline: 'none',
                    opacity: submittingComment ? 0.6 : 1
                  }}
                />
                <button type="submit" disabled={submittingComment} style={{
                  background: submittingComment ? '#999' : '#ff6b6b',
                  border: 'none',
                  borderRadius: '12px',
                  width: '46px',
                  color: 'white',
                  cursor: submittingComment ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Send size={18} />
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        /* Responsive design */
        @media (max-width: 768px) {
          .character-detail-grid {
            grid-template-columns: 1fr !important;
            gap: 24px !important;
          }
        }
      `}</style>

      {/* Login Modal */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLoginSuccess={() => {
          setShowLoginModal(false);
          // Retry starting chat after successful login
          handleStartChat();
        }}
      />
    </div>
  );
};

export default CharacterDetail;


